/**
 * iter87.471 (operator-requested) — retires the old auto-popup "Admin
 * Setup Guide" (AdminSetupGuide.jsx, deleted) in favour of a searchable
 * FAQ. The new-rivalry wizard + first-time dashboard walkthrough already
 * cover onboarding well; what was missing was an on-demand reference for
 * "how do I ___?" questions later, once an admin is already running
 * things day-to-day. Opened manually from the floating admin menu —
 * never auto-pops.
 */
import { useMemo, useState } from "react";
import { HelpCircle, Search, X } from "lucide-react";
import { BentoModal } from "@/components/onui/BentoMotion";
import TrophyTokensVideoInline from "../TrophyTokensVideoInline";
import { TrophyTokensWatchButton } from "../TrophyTokensVideoModal";
import ProPlusVideoModalLink from "../../pro/ProPlusVideoModalLink";

// Each entry's `keywords` widen the search match beyond the literal
// question/answer text (e.g. "scoring" should also surface under "points").
const FAQ_ENTRIES = [
    {
        q: "How do I invite new crewmates?",
        a: "Rivalry Admin → Invites tile — send email invites straight from there, or copy the rivalry's invite code and share it your way (text, group chat, whatever). Crewmates join once with a single personal code and it unlocks every tournament in the rivalry, now and future. Strict invites is always on, so only people invited here (or added directly via Crewmates) can join.",
        keywords: "join players add members",
    },
    {
        q: "How do I add, rename, or remove a crewmate directly?",
        a: "Rivalry Admin → Crewmates tile. You can add someone without an email invite, rename a crewmate, update their email, or look up their personal code. Removing a crewmate mid-season keeps their historical picks intact — they just drop off the leaderboard going forward.",
        keywords: "remove rename edit roster members delete",
    },
    {
        q: "How do I change the scoring / point values?",
        a: "Tournament Admin → Scoring tile, but what's actually editable depends on the template. Draft-based + playoff pick'em templates (MLB/NBA/MLS/UCL/NFL/CFB/NWSL Playoffs): Champion, Dark Horse, and Play-In point values are yours to set — but each template's own Match/Series Predictor engine is always fixed, never editable, regardless of lock state. Weekly Pick'em (NFL/EPL/La Liga Boost): scoring is entirely fixed at 6 / 2 / 0 / −2 (win-and-cover / win / cover / miss) — nothing to configure. Whatever IS editable for your template locks permanently the moment that template's own lock point hits (see \"When do picks, rosters, and settings actually lock?\"), so get it right ahead of time.",
        keywords: "points rules settings lock fixed engine champion dark horse",
    },
    {
        q: "How do I run the draft?",
        a: "Only on draft-based templates — Custom, March Madness, and World Cup Knockout Pick'em. Tournament Admin → Draft tile → Start Auto-Draft, once your crew has joined. It snakes picks across rounds so no one walks away with two top-tier teams; clear and re-run it as many times as you want right up until the first game kicks off — after that it locks for good. Every playoff pick'em template (MLB/NBA/MLS/UCL/NFL/CFB/NWSL Playoffs) and both Weekly Pick'em templates are draft-free — crewmates pick a Champion + Dark Horse (or weekly spreads) instead, nothing to draft. Golf position-draft tournaments auto-draft server-side at tee-off, so there's no manual CTA there either.",
        keywords: "teams pick auto-draft snake pickem no draft champion dark horse",
    },
    {
        q: "How do I enable Trophy Tokens?",
        a: "Pro+ only — Tournament Admin → Trophy Tokens tile. Set the pot (per-player buy-in or a fixed total, plus an optional house cut), then pick a payout style: Winner Take All or Podium (split 1st/2nd/3rd). Crewmates Opt In, then you Confirm each one — only Confirmed crewmates contribute and can win. The roster fully locks at whichever point your tournament's own lock trigger hits — first kickoff for most templates, weekly for Weekly Pick'em, tee-off for Golf (see \"When do picks, rosters, and settings actually lock?\") — any lingering un-confirmed opt-ins auto-drop right then. The winner is always auto-derived from the real leaderboard when you Finalize Awards — you never pick a winner by hand.",
        keywords: "pot prize money payout winner pro+",
        // iter87.536 (user-requested) — link out to the same walkthrough
        // video from this setup-focused entry too, not just the
        // dedicated "How do Trophy Tokens work?" entry below.
        watchVideoLink: true,
        // iter87.539 (user-requested) — also link out to the general
        // "See Pro+ in 30 seconds" promo video right where "Pro+ only"
        // is first mentioned, since this whole feature is gated on it.
        proPlusAnchor: "Pro+ only",
    },
    {
        q: "How do Trophy Tokens work?",
        video: true,
        summary: [
            { lead: "Set it up:", text: "in Tournament Admin, turn on Trophy Tokens, set the buy-in (per crewmate or fixed total, with an optional house cut), and choose Winner Take All or Podium (top 3). For Podium, set your splits and pick a fallback if there aren't enough players." },
            { lead: "Track who's in:", text: "the Pot Preview shows the pot. A red trophy means opted in but not committed, and a blue trophy means confirmed. Lock the pool early, or it locks at first kickoff." },
            { lead: "Monitor and finalize:", text: "follow the Trophy Race during the tournament, tap Finalize Awards when it ends, and optionally add the tokens to the rivalry's season-long totals." },
        ],
        a: "Watch the 90-second walkthrough above. In short: turn on Trophy Tokens and set the buy-in and payout style, track opt-ins and confirms via the Pot Preview, then finalize awards once the tournament ends and optionally add the tokens to the rivalry's season-long totals.",
        keywords: "video walkthrough tutorial how it works pot preview trophy race finalize opt in confirm",
    },
    {
        q: "How do I close a tournament?",
        a: "Tournament Admin → Danger Zone → Mark Complete / End. This permanently locks every edit for that tournament (scores, teams, scoring, live sync) and runs the final trophy pass. It's one-way from your side — only site support can reopen a completed tournament after that, so make sure it's really done.",
        keywords: "end finish lock complete",
    },
    {
        q: "How do I close or archive the whole rivalry?",
        a: "Rivalry Admin → Rivalry Settings → Danger Zone. Close makes it read-only for everyone — and on Pro+, you can Reopen it yourself anytime. On Free tier, closing happens automatically once your one tournament completes, and the rivalry fully archives (locked out for everyone, including you — only site support can undo it) 30 days later. If you're on Free and want to start a new rivalry sooner, Danger Zone also has an Archive Now button once it's closed, so you don't have to wait out the 30 days.",
        keywords: "reopen delete lock free tier pro+ limit",
        // iter87.539 (user-requested)
        proPlusAnchor: "and on Pro+",
    },
    {
        q: "How many rivalries can I run on Free?",
        a: "One at a time. Once your free rivalry closes (its one tournament completes), it auto-archives in 30 days — or you can archive it early from Danger Zone to free up your slot and start a new one sooner. Pro+ has no limit and never auto-archives.",
        keywords: "limit cap new rivalry start another",
        // iter87.539 (user-requested)
        proPlusAnchor: "Pro+",
    },
    {
        q: "How do I post an announcement to my crew?",
        a: "Rivalry Admin → Messages tile → compose and send. It posts straight to everyone's dashboard Smack Stream. You can also toggle whether crewmates are allowed to post to the Smack Stream themselves.",
        keywords: "message update smack stream broadcast",
    },
    {
        q: "How do I add a co-admin?",
        a: "Only a site admin can add or remove a co-admin — email admin@tinycrewrivalries.com to request one. Once they're added, use the Co-Admins tile (Rivalry Admin) to narrow exactly what they can touch — Scoring, Draft, Trophy Tokens, Invites, and more, individually.",
        keywords: "permissions helper assistant admin",
    },
    {
        q: "When do picks, rosters, and settings actually lock?",
        a: "Depends on the template. Draft-based (Custom / March Madness / World Cup KO): everything team/roster/scoring/draft-related locks the moment the season's FIRST GAME kicks off. Playoff pick'em (MLB/NBA/MLS/UCL/NFL/CFB/NWSL Playoffs): Champion/Dark Horse picks lock at the bracket's own first game — Wild Card, Round 1, or Knockout Play-offs, whichever applies — not the regular season. Weekly Pick'em (NFL/EPL/La Liga Boost): there's no single lock at all — each week's spreads lock independently right before that week's games, so the game stays open all season. Golf: locks at tee-off, not kickoff. Trophy Token opt-ins follow whichever of those applies to your tournament and must be Confirmed before that same point. After lock, the only way to unwind any of it is Danger Zone (where available) or a site-admin reopen.",
        keywords: "lock timing deadline before kickoff first game weekly boost tee off playoff pickem",
    },
    {
        q: "What's the difference between Rivalry Admin and Tournament Admin?",
        a: "Rivalry Admin covers whole-rivalry stuff — Crewmates, Invites, Messages, Co-Admins, and the rivalry-level Danger Zone. Tournament Admin covers one tournament at a time — Scoring, Draft, Trophy Tokens, Teams/Schedule (custom templates), Live Sync, and that tournament's own Danger Zone. Switch between the two anytime from the floating menu.",
        keywords: "scope switch rivalry vs tournament",
    },
    {
        q: "How do I check if live score syncing is working?",
        a: "Tournament Admin → Live Sync tile. A green \"Feed live\" badge means you're all set. If it ever shows amber, we're already on it — but you'll also see the last successful sync time there.",
        keywords: "espn api score feed status health",
    },
    {
        q: "How do I report a bug or suggest a feature?",
        a: "Floating menu (bottom-right) → Report an issue — works from any admin page, rivalry or tournament scope.",
        keywords: "feedback request idea bug issue contact",
    },
];

// iter87.539 (user-requested) — splits an entry's answer text right after
// `anchor` and drops in an inline "(see Pro+ in 30 seconds)" link to the
// general Pro+ promo video, so the Pro+ mention is backed by the same
// 30-second tour shown on the pricing page.
function renderAnswerWithProPlusLink(a, anchor, testId) {
    const idx = a.indexOf(anchor);
    if (idx === -1) return a;
    const splitAt = idx + anchor.length;
    return (
        <>
            {a.slice(0, splitAt)}{" "}
            <ProPlusVideoModalLink label="(see Pro+ in 30 seconds)" plain testId={testId} />
            {a.slice(splitAt)}
        </>
    );
}

export default function AdminFaqModal({ open, onClose }) {
    const [query, setQuery] = useState("");

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return FAQ_ENTRIES;
        return FAQ_ENTRIES.filter((e) =>
            e.q.toLowerCase().includes(q) ||
            e.a.toLowerCase().includes(q) ||
            e.keywords.toLowerCase().includes(q)
        );
    }, [query]);

    return (
        <BentoModal
            open={open}
            onClose={onClose}
            testId="admin-faq-modal"
            backdropClassName="items-start sm:items-center justify-center p-4 py-12 overflow-y-auto"
            panelClassName="brutal-card w-full max-w-2xl max-h-[85dvh] overflow-y-auto bg-ink border-2 border-cyber shadow-2xl my-auto"
        >
            <div className="sticky top-0 z-10 bg-ink border-b-2 border-zinc-800 px-5 py-4 space-y-3">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <HelpCircle className="w-5 h-5 text-cyber shrink-0" />
                        <h2 className="font-display font-extrabold text-xl uppercase tracking-tight truncate">Help &amp; FAQ</h2>
                    </div>
                    <button
                        type="button"
                        data-testid="admin-faq-close-btn"
                        onClick={onClose}
                        className="brutal-btn-ghost p-2 shrink-0"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="relative">
                    <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                        type="text"
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="How do I...?"
                        data-testid="admin-faq-search-input"
                        className="w-full bg-black/40 border border-zinc-700 focus:border-cyber rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-zinc-500 outline-none transition-colors"
                    />
                </div>
            </div>

            <div className="p-5 space-y-3" data-testid="admin-faq-results">
                {filtered.length === 0 ? (
                    <p className="text-sm text-zinc-500 text-center py-6" data-testid="admin-faq-no-results">
                        No matches for "{query}" — try{" "}
                        <button type="button" onClick={() => setQuery("")} className="text-cyber underline">
                            clearing the search
                        </button>
                        , or report it as a new question via "Report an issue".
                    </p>
                ) : (
                    filtered.map((entry, i) => (
                        <div key={i} className="brutal-card-light p-3" data-testid={`admin-faq-entry-${i}`}>
                            <p className="font-display font-extrabold text-sm uppercase tracking-tight text-cyber mb-1">
                                {entry.q}
                            </p>
                            {entry.video ? (
                                <div data-testid="admin-faq-trophy-tokens-video-entry">
                                    <TrophyTokensVideoInline />
                                    <ul className="space-y-1.5">
                                        {entry.summary.map((item, j) => (
                                            <li key={j} className="text-[13px] text-zinc-300 leading-relaxed">
                                                <b className="text-white">{item.lead}</b> {item.text}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ) : (
                                <>
                                    <p className="text-[13px] text-zinc-300 leading-relaxed">
                                        {entry.proPlusAnchor
                                            ? renderAnswerWithProPlusLink(entry.a, entry.proPlusAnchor, `admin-faq-entry-${i}-pro-plus-link`)
                                            : entry.a}
                                    </p>
                                    {entry.watchVideoLink && (
                                        <div className="mt-2" data-testid="admin-faq-enable-trophy-tokens-watch-link">
                                            <TrophyTokensWatchButton testId="admin-faq-enable-trophy-tokens-watch-btn" />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    ))
                )}
            </div>
        </BentoModal>
    );
}
